// src/app/features/health-travel/store/health-travel.effects.ts
//
// WHY a local inFlight set instead of checking store state:
// NgRx reducers run BEFORE effects for the same action. If the reducer sets
// loading:true on Load, the effect cannot use store state to detect duplicates.
//   • Static/cached lookups  → cache TTL check (isCacheValid) prevents re-fetch
//   • Search / list effects  → switchMap auto-cancels the previous pending call
//   • Keyed per-ID data      → local Set<string> in this class tracks in-flight calls

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, EMPTY } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { HealthTravelApiService } from '../services/health-travel-api.service';
import { isCacheValid } from './health-travel.state';
import {
  HtPlacesActions,
  HtSumInsuredActions,
  HtDurationsActions,
  HtBranchesActions,
  HtAgentsActions,
  HtStatesActions,
  HtPolicyListActions,
  HtPolicyTotalsActions,
  HtPolicyDetailActions,
} from './health-travel.actions';
import {
  selectPlaces,
  selectSumInsured,
  selectDurations,
  selectBranches,
  selectAgentsPerBranchMap,
  selectStatesPerPlaceMap,
  selectPolicyDetailsMap,
} from './health-travel.selectors';

@Injectable()
export class HealthTravelEffects {
  private readonly actions$   = inject(Actions);
  private readonly store      = inject(Store);
  private readonly api        = inject(HealthTravelApiService);
  private readonly translate  = inject(TranslateService);

  /**
   * Local in-flight set — prevents duplicate HTTP calls for keyed data
   * (per-place states, per-policy detail). Not stored in NgRx because
   * the reducer runs before effects, making store-based checks impossible.
   */
  private readonly _inFlight = new Set<string>();

  private readError(err: unknown): string {
    const e = err as any;
    return e?.error?.message ?? e?.message ?? this.translate.instant('ERRORS.UNEXPECTED_DOT');
  }

  // ── Places ────────────────────────────────────────────────────────────────
  loadPlaces$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HtPlacesActions.load),
      withLatestFrom(this.store.select(selectPlaces)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getInsurancePlaces().pipe(
          map(data => HtPlacesActions.success({ data })),
          catchError(err => of(HtPlacesActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );

  // ── SumInsuredConfig ──────────────────────────────────────────────────────
  loadSumInsured$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HtSumInsuredActions.load),
      withLatestFrom(this.store.select(selectSumInsured)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getSumInsuredConfig().pipe(
          map(data => HtSumInsuredActions.success({ data })),
          catchError(err => of(HtSumInsuredActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );

  // ── Durations ─────────────────────────────────────────────────────────────
  loadDurations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HtDurationsActions.load),
      withLatestFrom(this.store.select(selectDurations)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getTravelDurations().pipe(
          map(data => HtDurationsActions.success({ data })),
          catchError(err => of(HtDurationsActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );

  // ── Branches ──────────────────────────────────────────────────────────────
  loadBranches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HtBranchesActions.load),
      withLatestFrom(this.store.select(selectBranches)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getBranches().pipe(
          map(data => HtBranchesActions.success({ data })),
          catchError(err => of(HtBranchesActions.failure({ error: this.readError(err) }))),
        );
      }),
    )
  );

  // ── Agents per branch (keyed by branchId) ─────────────────────────────────
  loadAgents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HtAgentsActions.load),
      withLatestFrom(this.store.select(selectAgentsPerBranchMap)),
      switchMap(([{ branchId, allBranches, forceReload }, agentsMap]) => {
        const slice = agentsMap[branchId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `agents:${branchId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getAgents(allBranches, branchId).pipe(
          map(data => HtAgentsActions.success({ branchId, data })),
          catchError(err => of(HtAgentsActions.failure({ branchId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── States per destination place (keyed by placeId) ───────────────────────
  loadStates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HtStatesActions.load),
      withLatestFrom(this.store.select(selectStatesPerPlaceMap)),
      switchMap(([{ placeId, allStates, forceReload }, statesMap]) => {
        const slice = statesMap[placeId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `states:${placeId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getInsuranceStates(allStates, placeId).pipe(
          map(data => HtStatesActions.success({ placeId, data })),
          catchError(err => of(HtStatesActions.failure({ placeId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );

  // ── Policy list — switchMap cancels previous pending search ───────────────
  loadPolicyList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HtPolicyListActions.search),
      switchMap(({ request }) =>
        this.api.getPolicies(request).pipe(
          map(res => HtPolicyListActions.success({
            data:  res.rapPolicaSHNU,
            total: res.totalRreshta,
          })),
          catchError(err => of(HtPolicyListActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Policy totals — switchMap cancels previous pending call ────────────────
  loadPolicyTotals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HtPolicyTotalsActions.load),
      switchMap(({ request }) =>
        this.api.getPolicyTotals(request).pipe(
          map(res => HtPolicyTotalsActions.success({ data: res?.length ? res[0] : null })),
          catchError(err => of(HtPolicyTotalsActions.failure({ error: this.readError(err) }))),
        )
      ),
    )
  );

  // ── Policy detail (keyed by policyId) ─────────────────────────────────────
  loadPolicyDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HtPolicyDetailActions.load),
      withLatestFrom(this.store.select(selectPolicyDetailsMap)),
      switchMap(([{ policyId, forceReload }, detailsMap]) => {
        const slice = detailsMap[policyId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        const key = `detail:${policyId}`;
        if (this._inFlight.has(key)) return EMPTY;
        this._inFlight.add(key);
        return this.api.getPolicyDetail(policyId).pipe(
          map(data => HtPolicyDetailActions.success({ policyId, data })),
          catchError(err => of(HtPolicyDetailActions.failure({ policyId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(key)),
        );
      }),
    )
  );
}
